import { X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { PhotoVideoConsent, UserConsents } from '../../services/consentApi';
import Button from '../ui/Button';

export type ConsentType = 'photoVideo' | 'waiver' | 'authorization' | 'all';

interface ConsentFormModalProps {
  currentConsents: UserConsents | null;
  consentType?: ConsentType;
  onClose: () => void;
  onSubmit: (data: {
    photoVideoConsent: PhotoVideoConsent;
    liabilityWaiverAgreed: boolean;
    parentalReleaseAgreed: boolean;
  }) => Promise<void>;
}

export default function ConsentFormModal({
  currentConsents,
  consentType = 'all',
  onClose,
  onSubmit,
}: ConsentFormModalProps) {
  const { t } = useTranslation(['dashboard']);

  const [photoVideoConsent, setPhotoVideoConsent] = useState<PhotoVideoConsent | ''>('');
  const [liabilityWaiverAgreed, setLiabilityWaiverAgreed] = useState(false);
  const [parentalReleaseAgreed, setParentalReleaseAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load current consents
  useEffect(() => {
    if (currentConsents) {
      setPhotoVideoConsent(currentConsents.photoVideoConsent || '');
      setLiabilityWaiverAgreed(currentConsents.liabilityWaiverAgreed);
      setParentalReleaseAgreed(currentConsents.parentalReleaseAgreed);
    }
  }, [currentConsents]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation based on consent type
    if ((consentType === 'photoVideo' || consentType === 'all') && !photoVideoConsent) {
      setError(t('dashboard:consent.form.errors.selectPhotoOption'));
      return;
    }

    if ((consentType === 'waiver' || consentType === 'all') && !liabilityWaiverAgreed) {
      setError(t('dashboard:consent.form.errors.mustAgreeWaiver'));
      return;
    }

    if ((consentType === 'authorization' || consentType === 'all') && !parentalReleaseAgreed) {
      setError(t('dashboard:consent.form.errors.mustAgreeRelease'));
      return;
    }

    try {
      setIsSubmitting(true);
      await onSubmit({
        photoVideoConsent: photoVideoConsent as PhotoVideoConsent,
        liabilityWaiverAgreed,
        parentalReleaseAgreed,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save consents');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="relative inline-block w-full max-w-3xl px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:p-6">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {consentType === 'photoVideo' && t('dashboard:consent.photoVideo.title')}
              {consentType === 'waiver' && t('dashboard:consent.waiver.title')}
              {consentType === 'authorization' && t('dashboard:consent.authorization.title')}
              {consentType === 'all' && t('dashboard:consent.form.title')}
            </h2>
            {consentType === 'all' && (
              <p className="mt-2 text-sm text-gray-600">{t('dashboard:consent.form.subtitle')}</p>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Photo & Video Consent */}
            {(consentType === 'photoVideo' || consentType === 'all') && (
              <div className={consentType === 'all' ? 'pb-6' : 'pb-2'}>
                {consentType === 'all' && (
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('dashboard:consent.photoVideo.title')}
                  </h3>
                )}
                <div className="prose prose-sm max-w-none mb-4">
                  <p className="text-gray-700 whitespace-pre-line">
                    {t('dashboard:consent.photoVideo.fullText')}
                  </p>
                </div>

                <p className="text-sm font-medium text-gray-900 mb-3">
                  {t('dashboard:consent.form.selectOption')}
                </p>

                <div className="space-y-3">
                  <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="photoVideoConsent"
                      value="FULL"
                      checked={photoVideoConsent === 'FULL'}
                      onChange={(e) => setPhotoVideoConsent(e.target.value as PhotoVideoConsent)}
                      className="mt-1 h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    />
                    <div className="ml-3 flex-1">
                      <div className="text-sm text-gray-700 whitespace-pre-line">
                        {t('dashboard:consent.form.optionA')}
                      </div>
                    </div>
                  </label>

                  <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="photoVideoConsent"
                      value="BLURRED"
                      checked={photoVideoConsent === 'BLURRED'}
                      onChange={(e) => setPhotoVideoConsent(e.target.value as PhotoVideoConsent)}
                      className="mt-1 h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    />
                    <div className="ml-3 flex-1">
                      <div className="text-sm text-gray-700 whitespace-pre-line">
                        {t('dashboard:consent.form.optionB')}
                      </div>
                    </div>
                  </label>

                  <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="photoVideoConsent"
                      value="NO_PHOTO"
                      checked={photoVideoConsent === 'NO_PHOTO'}
                      onChange={(e) => setPhotoVideoConsent(e.target.value as PhotoVideoConsent)}
                      className="mt-1 h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    />
                    <div className="ml-3 flex-1">
                      <div className="text-sm text-gray-700 whitespace-pre-line">
                        {t('dashboard:consent.form.optionC')}
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Activity Waiver */}
            {(consentType === 'waiver' || consentType === 'all') && (
              <div className={consentType === 'all' ? 'pb-6' : 'pb-2'}>
                {consentType === 'all' && (
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('dashboard:consent.waiver.title')}
                  </h3>
                )}
                <div className="prose prose-sm max-w-none mb-4">
                  <p className="text-gray-700 whitespace-pre-line">
                    {t('dashboard:consent.waiver.fullText')}
                  </p>
                </div>

                <label className="flex items-start p-4 bg-gray-50 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={liabilityWaiverAgreed}
                    onChange={(e) => setLiabilityWaiverAgreed(e.target.checked)}
                    className="mt-1 h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <div className="ml-3 flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {t('dashboard:consent.form.agreeWaiver')}
                    </div>
                  </div>
                </label>
              </div>
            )}

            {/* Parental Release */}
            {(consentType === 'authorization' || consentType === 'all') && (
              <div className="pb-2">
                {consentType === 'all' && (
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('dashboard:consent.authorization.title')}
                  </h3>
                )}
                <div className="prose prose-sm max-w-none mb-4">
                  <p className="text-gray-700 whitespace-pre-line">
                    {t('dashboard:consent.authorization.fullText')}
                  </p>
                </div>

                <label className="flex items-start p-4 bg-gray-50 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={parentalReleaseAgreed}
                    onChange={(e) => setParentalReleaseAgreed(e.target.checked)}
                    className="mt-1 h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <div className="ml-3 flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {t('dashboard:consent.form.agreeRelease')}
                    </div>
                  </div>
                </label>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex items-center justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                {t('dashboard:consent.form.cancel')}
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting
                  ? t('dashboard:consent.form.saving')
                  : t('dashboard:consent.form.save')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
